import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowUpCircle, ArrowLeft, BadgeCheck } from "lucide-react";

const UPGRADE_FEE = 30;
const FREE_TIERS = ["Gold Voyager", "Platinum Islander"];

export default function Upgrade() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [selected, setSelected] = useState("");
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (bookingId) {
      api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch((e) => setError(errMsg(e)));
    }
  }, [bookingId]);

  useEffect(() => {
    if (booking) {
      api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
      api.get("/loyalty/status", { params: { email: booking.customer_email } }).then((r) => setLoyalty(r.data)).catch(() => {});
    }
  }, [booking]);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.get("/bookings/lookup", { params: { ref, email } });
      setBooking(data);
      localStorage.setItem(`booking_email_${data.id}`, email.trim().toLowerCase());
    } catch (e2) {
      setError(errMsg(e2));
    }
  };

  const options = useMemo(() => {
    if (!booking) return [];
    return vehicles.filter((v) => v.id !== booking.vehicle_id && v.active !== false && v.price_per_day > booking.daily_rate);
  }, [vehicles, booking]);

  const freeUpgrade = loyalty && FREE_TIERS.includes(loyalty.tier);
  const chosen = options.find((v) => v.id === selected);
  const days = booking?.days || 1;
  const diff = chosen ? chosen.price_per_day - booking.daily_rate : 0;
  const base = diff * days;
  const processing = (base + UPGRADE_FEE) * 0.03;
  const amount = base + UPGRADE_FEE + processing;

  const submit = async () => {
    if (!chosen) return toast.error("Choose a vehicle to upgrade to");
    setLoading(true);
    try {
      const stored = localStorage.getItem(`booking_email_${booking.id}`) || email;
      const { data } = await api.post(`/bookings/${booking.id}/upgrade`, {
        new_vehicle_id: chosen.id,
        origin_url: window.location.origin,
        email: user ? undefined : stored,
      });
      if (data.free_upgrade) {
        setDone({ vehicle: chosen.name, tier: data.tier });
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e2) {
      toast.error(errMsg(e2));
      setLoading(false);
    }
  };

  if (!booking) {
    return (
      <div data-testid="upgrade-lookup" className="max-w-md mx-auto px-6 pt-40 pb-24">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Vehicle Upgrade</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
          Upgrade your <span className="italic gold-text">ride</span>
        </h1>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Move to a higher-class vehicle — pay only the price difference plus a $30 upgrade service fee, subject to availability.
        </p>
        {bookingId && error && <p className="mt-4 text-sm text-destructive font-ui">{error} — find your booking manually below.</p>}
        <form onSubmit={lookup} className="mt-8 space-y-5">
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">Booking reference (e.g. ED-1A2B3C)</Label>
            <Input data-testid="upgrade-ref-input" required value={ref} onChange={(e) => setRef(e.target.value)} className="input-dark mt-2" />
          </div>
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">Email used at booking</Label>
            <Input data-testid="upgrade-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-2" />
          </div>
          {error && !bookingId && <p data-testid="upgrade-lookup-error" className="text-sm text-destructive font-ui">{error}</p>}
          <Button data-testid="upgrade-lookup-button" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
            Find My Booking
          </Button>
        </form>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="upgrade-success" className="max-w-xl mx-auto px-6 pt-40 pb-24 text-left">
        <div className="bg-card border border-border rounded-2xl p-10">
          <BadgeCheck size={40} className="gold-text" />
          <h1 className="mt-5 font-display text-4xl tracking-tight">Upgrade <span className="italic gold-text">confirmed</span></h1>
          <p className="mt-3 text-sm text-muted-foreground font-ui leading-relaxed">
            You're now driving the <span className="text-foreground font-semibold">{done.vehicle}</span> — free of charge as a {done.tier} loyalty perk.
            A confirmation email is on its way. Enjoy the ride!
          </p>
          <Link to={user ? "/account" : "/"}>
            <Button data-testid="upgrade-success-back" className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
              {user ? "Back to My Bookings" : "Back to Home"}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="upgrade-page" className="max-w-xl mx-auto px-6 pt-36 pb-24">
      <Link to={user ? "/account" : "/"} className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2">
        <ArrowLeft size={15} /> Back
      </Link>
      <div className="mt-8 bg-card border border-border rounded-2xl p-8">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
          <ArrowUpCircle size={14} className="gold-text" /> Vehicle Upgrade
        </p>
        <h1 className="mt-4 font-display text-3xl tracking-tight">{booking.vehicle_name}</h1>
        <p className="mt-2 text-sm text-muted-foreground font-ui">
          {booking.booking_ref} · {fmtDate(booking.start_date)} → {fmtDate(booking.end_date)} · {days} {days === 1 ? "day" : "days"} · current rate {fmtMoney(booking.daily_rate)}/day
        </p>

        {freeUpgrade && (
          <p data-testid="upgrade-free-banner" className="mt-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-xs font-ui text-foreground/80">
            {loyalty.tier} perk — vehicle upgrades are <span className="gold-text font-semibold">FREE</span> for you, subject to availability.
          </p>
        )}

        <p className="mt-6 font-ui text-xs tracking-wide text-muted-foreground">Choose your upgrade (subject to availability)</p>
        {options.length === 0 ? (
          <p data-testid="upgrade-none" className="mt-3 text-sm text-muted-foreground font-ui">No higher-class vehicles available right now — message us on WhatsApp and we'll help.</p>
        ) : (
          <div className="mt-3 space-y-2.5">
            {options.map((v) => (
              <button
                key={v.id}
                type="button"
                data-testid={`upgrade-vehicle-option-${v.id}`}
                onClick={() => setSelected(v.id)}
                className={`w-full flex items-center justify-between gap-4 rounded-xl border px-5 py-4 text-left transition-colors duration-200 ${selected === v.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              >
                <span>
                  <span className="block font-ui font-medium">{v.name}</span>
                  <span className="block text-xs text-muted-foreground font-ui mt-0.5">{v.category}</span>
                </span>
                <span className="text-sm font-ui gold-text font-semibold whitespace-nowrap">+{fmtMoney(v.price_per_day - booking.daily_rate)}/day</span>
              </button>
            ))}
          </div>
        )}

        {chosen && !freeUpgrade && (
          <div data-testid="upgrade-price-summary" className="mt-6 border border-border rounded-xl p-5 space-y-2 text-sm font-ui">
            <div className="flex justify-between"><span className="text-muted-foreground">Price difference ({days} {days === 1 ? "day" : "days"} × {fmtMoney(diff)})</span><span>{fmtMoney(base)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Upgrade service fee</span><span>{fmtMoney(UPGRADE_FEE)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Processing fee (3%)</span><span>{fmtMoney(processing)}</span></div>
            <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Due now</span><span className="gold-text text-xl font-semibold">{fmtMoney(amount)}</span></div>
          </div>
        )}
        {chosen && freeUpgrade && (
          <div data-testid="upgrade-price-summary" className="mt-6 border border-primary/30 rounded-xl p-5 space-y-2 text-sm font-ui bg-primary/5">
            <div className="flex justify-between"><span className="text-muted-foreground">Price difference + service fee</span><span className="line-through text-muted-foreground">{fmtMoney(amount)}</span></div>
            <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Due now — {loyalty.tier} perk</span><span className="gold-text text-xl font-semibold">FREE</span></div>
          </div>
        )}
        <Button data-testid="upgrade-pay-button" disabled={!chosen || loading} onClick={submit} className="mt-6 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
          {loading ? "Processing…" : freeUpgrade ? "Claim Free Upgrade" : "Pay Difference & Upgrade"}
        </Button>
        <p className="mt-3 text-xs text-muted-foreground font-ui">
          {freeUpgrade
            ? "Instant confirmation — your rental agreement updates automatically to the new vehicle."
            : "Instant confirmation by secure card checkout. Your rental agreement updates automatically to the new vehicle."}
        </p>
      </div>
    </div>
  );
}
